import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Images, ChevronDown } from "lucide-react";

// Add real photos here: { src: "/photos/shot1.jpg", caption: "Game night" }
const PHOTOS = [
  { src: "/jb-test.jpeg", caption: "Game night setup" },
  { src: "/jb-test.jpeg", caption: "Ripping packs" },
  { src: "/jb-test.jpeg", caption: "Manager Cards in play" },
  { src: "/jb-test4.jpeg", caption: "The diamond" },
];

export default function Gallery() {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);

  const prev = () => setActive((i) => (i - 1 + PHOTOS.length) % PHOTOS.length);
  const next = () => setActive((i) => (i + 1) % PHOTOS.length);

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-3 bg-slate-950/80 backdrop-blur-md border-b border-slate-800/50">
        <Link to="/" className="flex items-center gap-2 font-black text-lg tracking-tight text-white">
          <img src="/favicons32x32.png" alt="" className="w-5 h-5" />
          JUNK <span className="text-amber-400">BALL</span>
        </Link>
        <div className="flex items-center gap-4">
          <Link to="/print-and-play" className="text-xs text-slate-400 hover:text-white transition-colors font-medium">Print & Play</Link>
          <Link to="/contact" className="text-xs text-slate-400 hover:text-white transition-colors font-medium">Contact</Link>
          <button
            onClick={() => navigate("/online-setup")}
            className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs transition-all"
          >
            Play Now
          </button>
        </div>
      </nav>

      {/* Content */}
      <div className="flex-1 flex flex-col px-6 pt-24 pb-10 max-w-5xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col flex-1 gap-6"
        >
          {/* Header */}
          <div className="flex items-center gap-3">
            <Images size={20} className="text-amber-400" />
            <h1 className="text-2xl font-black">Gallery</h1>
            <span className="text-slate-600 text-xs font-bold uppercase tracking-widest ml-1">
              {active + 1} / {PHOTOS.length}
            </span>
          </div>

          {/* Main frame */}
          <div className="relative w-full aspect-video rounded-2xl overflow-hidden border border-slate-800 bg-slate-900 group">
            <AnimatePresence mode="wait">
              <motion.img
                key={active}
                src={PHOTOS[active].src}
                alt={PHOTOS[active].caption}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="w-full h-full object-cover"
              />
            </AnimatePresence>

            {/* Caption */}
            <div className="absolute bottom-0 inset-x-0 px-6 py-4 bg-gradient-to-t from-black/70 to-transparent">
              <p className="text-sm font-bold text-white">{PHOTOS[active].caption}</p>
            </div>

            {/* Arrows */}
            <button
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-black/70 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
            >
              <ChevronDown size={18} className="text-white rotate-90" />
            </button>
            <button
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-black/70 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
            >
              <ChevronDown size={18} className="text-white -rotate-90" />
            </button>
          </div>

          {/* Filmstrip */}
          <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
            {PHOTOS.map((photo, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`flex-shrink-0 w-28 h-20 rounded-xl overflow-hidden border transition-all duration-200 ${
                  active === i
                    ? "border-amber-400 opacity-100 scale-[1.03]"
                    : "border-slate-800 opacity-50 hover:opacity-80 hover:border-slate-600"
                }`}
              >
                <img src={photo.src} alt={photo.caption} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
